/**
 * SCRIPT mode — THE SHARE LINK'S PURE HALF (split out job #763, 2026-09-23).
 *
 * Building and reading a `?script=<lang>&item=<id>` link needs nothing but
 * URLSearchParams, and every cold boot reads it — App.vue and LearningPlayer
 * both ask "was this visit a shared Script link?". It lives here, with NO
 * heavy imports (modeName is dependency-free), so answering that question never drags the Script ladders
 * into the entry chunk every stranger downloads. Resolving
 * the language to a course uses the lightweight availability lists in
 * shareLink.ts and is imported on demand, only when the param is present.
 * The scheme itself is documented in shareLink.ts.
 */
import { isBuildMode } from './modeName'

/** The query param that names the ladder. */
export const SCRIPT_PARAM = 'script'
/**
 * The same door under its Build name (Tom, 2026-10-28). A tiles-card language
 * (Welsh, Irish, Italian) shares `?build=<lang>`; every `?script=<lang>` link
 * already out there keeps working, because parse reads both.
 */
export const BUILD_PARAM = 'build'
/** The query param that names the item on it. */
export const ITEM_PARAM = 'item'
/** The query param that opts into a course's topic deck (job #103): `?script=ita&deck=environment-conversation`. */
export const DECK_PARAM = 'deck'

export interface ScriptShareTarget {
  /** Three-letter target language: ara, zho, kor, jpn. */
  lang: string
  /** The item's own id. Absent shares the ladder's opening. */
  itemId?: string | null
  /** A topic deck's slug (welshScript.ts TOPIC_DECKS). Absent opens the main deck. */
  deck?: string | null
}

/**
 * The link itself. `origin` is a bare origin — `https://staging.saysomethingin.app`
 * — and the path is always the player's root, which is the only door Script
 * mode has.
 */
export function scriptShareUrl(origin: string, target: ScriptShareTarget): string {
  const base = origin.replace(/\/+$/, '')
  const params = new URLSearchParams()
  params.set(isBuildMode(target.lang) ? BUILD_PARAM : SCRIPT_PARAM, target.lang)
  if (target.deck) params.set(DECK_PARAM, target.deck)
  if (target.itemId) params.set(ITEM_PARAM, target.itemId)
  return `${base}/?${params.toString()}`
}

/**
 * The other direction: what a landing browser was asked for. Returns null for
 * a URL that names no ladder, so every non-Script visit costs one missing key
 * and nothing else.
 */
export function parseScriptShare(search: string): ScriptShareTarget | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  const lang = (params.get(SCRIPT_PARAM) || params.get(BUILD_PARAM) || '').trim().toLowerCase().slice(0, 3)
  if (!lang) return null
  const itemId = params.get(ITEM_PARAM)
  const deck = (params.get(DECK_PARAM) || '').trim()
  return deck ? { lang, itemId: itemId || null, deck } : { lang, itemId: itemId || null }
}
